import { Box, Collapse } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetCustomerNamesWithProjectPlanningAccess } from 'src/api/customer'
import { useGetAuthorizedProjects } from 'src/api/project'
import { AppRoutesNavigation } from 'src/app-routes'
import { CustomerHeader } from 'src/components/sidemenu/CustomerHeader'
import ProjectHeader from 'src/components/sidemenu/ProjectHeader'
import { ICustomerNameWithPermission } from 'src/generated-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CenteredSpinner } from 'src/components/Spinner'
import { SearchBar } from 'src/components/common/input/SearchBar'
import { containsProjectId } from './versions/projectVersion.action'

export const ProjectSideBar = () => {
  const { customerId } = useParams()
  const { data, isLoading } = useGetCustomerNamesWithProjectPlanningAccess()
  const [searchInput, setSearchInput] = useState('')
  const { t } = useTranslation(['common', 'projects'])

  const filteredCustomers = useMemo(() => {
    if (data && customerId != undefined) {
      const selectedCustomerId = data.findIndex(c => c.id == parseInt(customerId))
      const customerArray = [data[selectedCustomerId]]
      data.map(cus => {
        if (cus.id != parseInt(customerId)) {
          customerArray.push(cus)
        }
      })
      return customerArray
    } else {
      return []
    }
  }, [data, customerId])

  const selectedCustomer = data?.find(c => c.id == (customerId as EntityId))

  if (isLoading) return <CenteredSpinner />

  return (
    <>
      <Box overflow={'hidden'}>
        {selectedCustomer && (
          <>
            <Box sx={{ padding: '0px 12px' }}>
              <SearchBar
                searchInput={searchInput}
                placeholder={t('search', { ns: 'common' })}
                width={'100%'}
                onChange={setSearchInput}
                marginBottom={'5px'}
              />
            </Box>
            <Box height={'calc(100vh - 150px)'} className="overflow-y-auto hide-scrollbar">
              {filteredCustomers.map(c => (
                <CustomerHeaderContainer customer={c} key={c.id} searchInput={searchInput} />
              ))}
            </Box>
          </>
        )}
      </Box>
    </>
  )
}

export default ProjectSideBar

const CustomerHeaderContainer: React.FC<{
  customer: ICustomerNameWithPermission
  searchInput: string
}> = ({ customer, searchInput }) => {
  const { customerId, projectId } = useParams()
  const [open, setOpen] = useState(customer.id == (customerId as EntityId))
  const { data, isLoading } = useGetAuthorizedProjects(customer.id, searchInput, true)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const selectedProject = data?.find(p => containsProjectId(p, projectId))

  if (data) {
    data.sort((a, b) => {
      if (a.status !== b.status) {
        return a.status - b.status
      }
      return a.name.localeCompare(b.name)
    })
    if (selectedProject) {
      const index = data.indexOf(selectedProject)
      data.splice(index, 1)
      data.unshift(selectedProject)
    }
  }

  useEffect(() => {
    setOpen(searchInput.length > 0 ? true : customer.id == (customerId as EntityId))
  }, [customerId, searchInput, customer.id])

  return (
    <Box>
      <Box padding={'0px 20px 20px 12px'} onClick={() => setOpen(!open)}>
        <CustomerHeader
          hasAdminAccess={customer.isAdminCustomer}
          image={customer.image ?? ''}
          {...customer}
          isGlobalServiceStationProvider={false}
          isLandingPage={false}
          isProject={true}
        />
      </Box>
      <Box>
        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <Collapse in={open}>
            <Box sx={{ padding: '6px 12px' }}>
              {data?.length ? (
                data.map(p => (
                  <ProjectHeader
                    key={p.id}
                    customerId={customer.id}
                    project={p}
                    onClick={e => {
                      e.stopPropagation()
                      navigate(AppRoutesNavigation.Project.project(p.customerId, p.localityId, p.id))
                    }}
                    active={containsProjectId(p, projectId)}
                  />
                ))
              ) : (
                <Box className="font-semibold text-center text-gray-600 text-l">
                  {t('thisCustomerHasNoProjects', { ns: 'sidebar' })}
                </Box>
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  )
}
